import { SSR_QUERY } from '@/router/query-params'
import { idleCallbackPromise } from '@/utils/helpers'

import { capRecordData } from './cap-data'
import { watchCornerTaps } from './corner-taps'
import type { DevtoolsPanel, DevtoolsRecord, DevtoolsSource } from './types'
import { type DevtoolsUiState, enableUiState, hasDevtoolsQuery, resolveUiState } from './ui-state'

const MAX_RECORDS = 2000

type PanelSource = {
    load: () => Promise<DevtoolsPanel>
    level: 'partner' | 'full'
    isLoading: boolean
    panel?: DevtoolsPanel
}

export class Devtools {
    readonly records: DevtoolsRecord[] = []

    readonly panels = new Map<string, DevtoolsPanel>()

    private readonly listeners = new Set<() => void>()

    private readonly recordReaders = new Set<(record: DevtoolsRecord) => void>()

    private lastRecordId = 0

    private readonly adRequestIds = new WeakMap<object, number>()

    private readonly panelSources = new Set<PanelSource>()

    private isOpened = false

    full = false

    markOpened(full: boolean) {
        this.isOpened = true
        this.full = full
        this.loadPanels()
    }

    record(source: DevtoolsSource, name: string, data: Record<string, unknown> = {}) {
        const record: DevtoolsRecord = {
            id: ++this.lastRecordId,
            time: performance.now(),
            source,
            name,
            data: capRecordData(data),
            adRequestId: this.adRequestIds.get(data),
        }
        this.records.push(record)
        this.recordReaders.forEach((reader) => reader(record))
        if (this.records.length > MAX_RECORDS) {
            this.records.shift()
        }
        this.notify()
    }

    markAdRequest(event: object, adRequestId: number) {
        this.adRequestIds.set(event, adRequestId)
    }

    readRecords(reader: (record: DevtoolsRecord) => void): () => void {
        this.records.forEach(reader)
        this.recordReaders.add(reader)
        return () => this.recordReaders.delete(reader)
    }

    addPanel(load: () => Promise<DevtoolsPanel>, level: 'partner' | 'full'): () => void {
        const source: PanelSource = { load, level, isLoading: false }
        this.panelSources.add(source)
        this.loadPanels()
        return () => {
            this.panelSources.delete(source)
            if (source.panel && this.panels.get(source.panel.id) === source.panel) {
                this.panels.delete(source.panel.id)
                this.notify()
            }
        }
    }

    private loadPanels() {
        this.panelSources.forEach((source) => {
            if (source.isLoading || !this.isOpened || (source.level === 'full' && !this.full)) {
                return
            }
            source.isLoading = true
            source.load().then((panel) => {
                if (this.panelSources.has(source)) {
                    source.panel = panel
                    this.panels.set(panel.id, panel)
                    this.notify()
                }
            })
        })
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    private notify() {
        this.listeners.forEach((listener) => listener())
    }
}

export function getDevtools(): Devtools | undefined {
    return import.meta.env.SSR ? undefined : window.pgDevtools
}

export function initDevtools() {
    if (window.pgDevtools) {
        return
    }
    const devtools = new Devtools()
    window.pgDevtools = devtools

    let isShown = false
    const show = (uiState: DevtoolsUiState) => {
        isShown = true
        devtools.markOpened(uiState.full)
        const pageLoaded =
            document.readyState === 'complete'
                ? Promise.resolve()
                : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }))

        pageLoaded
            .then(() => idleCallbackPromise())
            .then(() => import('./ui/mount'))
            .then(({ mountDevtools }) =>
                mountDevtools(devtools, uiState, () => {
                    isShown = false
                }),
            )
    }
    const open = (full: boolean) => {
        if (devtools.full && !full) {
            // the full panels are already loaded, and only a new page drops them
            enableUiState(full)
            if (hasDevtoolsQuery()) {
                reloadWithQuery(SSR_QUERY.DEVTOOLS, '1')
            } else {
                window.location.reload()
            }
        } else if (!isShown || devtools.full !== full) {
            show(enableUiState(full))
        }
    }
    window.pgOpenDevtools = (level) => open(level === 'full')
    watchCornerTaps(open)
    const storedUiState = resolveUiState()
    if (storedUiState) {
        show(storedUiState)
    }
}

export function reloadWithQuery(param: string, value: string) {
    const params = new URLSearchParams(window.location.search)
    params.set(param, value)
    window.location.search = params.toString()
}
